import { useEffect, useRef } from "react";

const NON_TYPING_INPUT_TYPES = ["checkbox", "radio", "button", "submit", "reset"];

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target.isContentEditable || target.tagName === "TEXTAREA" || target.tagName === "SELECT") {
    return true;
  }
  if (target instanceof HTMLInputElement) {
    return !NON_TYPING_INPUT_TYPES.includes(target.type);
  }
  return false;
}

function isActivatable(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && (target.tagName === "BUTTON" || target.tagName === "A");
}

export function useKeyboardShortcuts(bindings: Record<string, () => void>, isEnabled = true) {
  const bindingsRef = useRef(bindings);

  useEffect(() => {
    bindingsRef.current = bindings;
  });

  useEffect(() => {
    if (!isEnabled) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey || event.repeat) {
        return;
      }
      const hasCommandKey = event.ctrlKey || event.metaKey;
      if (isTypingTarget(event.target) !== hasCommandKey) {
        return;
      }
      if (event.key === "Enter" && isActivatable(event.target)) {
        return;
      }
      const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
      const handler = bindingsRef.current[key];
      if (!handler) {
        return;
      }
      event.preventDefault();
      handler();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isEnabled]);
}
